import { useContext, useEffect, useState } from 'react';
import { ApiFetcherContext } from 'all-in-one-shared-api';
import FormData from 'form-data';
import useSWR, { Key, SWRConfiguration } from 'swr';

import { FileUploadResponse, FileUploadResponseObjForCI, ServiceReturnType } from './interfaces';

export const API_PATH = '/xapi/v1/life-claim/document';

const CLIENT_TYPE = 'web';
const CALL_BPM_FLAG = 'LIFECLAIMFLAG';

export enum UploadFileType {
  CRITICAL = 'critical',
  PROOF = 'proof',
  LABORATORY = 'laboratory',
  OTHER = 'other',
}

export interface ExistingCounts {
  critical?: number;
  proof?: number;
  laboratory?: number;
  other?: number;
}

export const useUploadFilesForCI = (
  key: Key,
  accessToken: string,
  referenceNo: string,
  policyNumber: string,
  criticalFiles: File[],
  proofFiles: File[],
  laboratoryFiles: File[],
  otherFiles: File[],
  existingCounts: ExistingCounts = {},
  config?: SWRConfiguration,
): ServiceReturnType<FileUploadResponseObjForCI> => {
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const apiFetcher = useContext(ApiFetcherContext);
  const options = {
    headers: {
      Authorization: 'Bearer ' + accessToken,
      'Content-Type': 'multipart/form-data',
    },
  };

  const uploadFile = async (
    file: File,
    index: number,
    uploadFileType: string,
    isLast: boolean,
  ): Promise<FileUploadResponse> => {
    const formDataObj = new FormData();
    formDataObj.append('file', file);

    return await apiFetcher.post<FileUploadResponse>(API_PATH, formDataObj, {
      ...options,
      params: {
        referenceNo,
        index,
        isLast,
        policyNumber,
        clientType: CLIENT_TYPE,
        callBpmFlag: CALL_BPM_FLAG,
        uploadFileType,
      },
    });
  };

  const uploadFiles = async (
    uploadFileType: string,
    files: File[] | undefined,
    startIndex: number,
    lastFileType?: boolean,
  ): Promise<FileUploadResponse[] | null> => {
    if (!files || files.length === 0) return null;
    const responses: FileUploadResponse[] = [];
    for (let i = 0; i < files.length; i++) {
      const isLast = !!lastFileType && i === files.length - 1;
      // 序号接续已存在的旧文件：从 startIndex + 1 开始
      const response = await uploadFile(files[i], startIndex + i + 1, uploadFileType, isLast);
      responses.push(response);
    }
    return responses;
  };

  const makeRequests = async (): Promise<FileUploadResponseObjForCI> => {
    // 找到所有实际有文件的类型组，最后一组即为真正需要标记 isLast 的类型
    const fileGroups = [
      { type: UploadFileType.CRITICAL, files: criticalFiles },
      { type: UploadFileType.PROOF, files: proofFiles },
      { type: UploadFileType.LABORATORY, files: laboratoryFiles },
      { type: UploadFileType.OTHER, files: otherFiles },
    ].filter((g) => g.files && g.files.length > 0);
    const lastType = fileGroups[fileGroups.length - 1]?.type;

    const critical = await uploadFiles(
      UploadFileType.CRITICAL,
      criticalFiles,
      existingCounts.critical ?? 0,
      lastType === UploadFileType.CRITICAL,
    );
    const proof = await uploadFiles(
      UploadFileType.PROOF,
      proofFiles,
      existingCounts.proof ?? 0,
      lastType === UploadFileType.PROOF,
    );
    const laboratory = await uploadFiles(
      UploadFileType.LABORATORY,
      laboratoryFiles,
      existingCounts.laboratory ?? 0,
      lastType === UploadFileType.LABORATORY,
    );
    const other = await uploadFiles(
      UploadFileType.OTHER,
      otherFiles,
      existingCounts.other ?? 0,
      lastType === UploadFileType.OTHER,
    );
    return { critical, proof, laboratory, other };
  };

  const { data, error, isValidating } = useSWR(() => (shouldFetch ? key : null), makeRequests, config);

  // 移到 useEffect 中，确保组件先拿到 data 完成处理，再重置 shouldFetch
  // 避免在 render 中 setState 导致下次 render 时 SWR key 为 null 从而清空 data
  useEffect(() => {
    if (data) {
      setShouldFetch(false);
    }
  }, [data]);

  return { data, error, shouldFetch: setShouldFetch, isLoading: isValidating };
};
